import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png';
import { FaAngleLeft } from 'react-icons/fa6';
import { FaEnvelopeOpenText, FaLock, FaShieldAlt } from 'react-icons/fa';
import API_ROUTES from '../api/apiUri';
import { apiCall } from '../api/apiCall';
import { validateEmailOrPhone } from '../utils/formValidationUtils';

const ForgotPassword = () => {
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldError, setFieldError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationMessage = validateEmailOrPhone(emailOrPhone);
    if (validationMessage) {
      setFieldError(validationMessage);
      return;
    }

    setLoading(true);
    setError('');
    setFieldError('');
    try {
      try {
        const data = await apiCall({
          url: API_ROUTES.employee.employee_forgot_password_request,
          method: 'POST',
          data: { EmailOrPhone: emailOrPhone.trim() },
          headers: { 'Content-Type': 'application/json' },
        });
        navigate('/verify-code', { state: { resetTokenId: data.resetTokenId, emailOrPhone } });
      } catch (err) {
        if (err?.response?.status === 404) {
          setError(err.response.data?.message);
        } else {
          setError(err?.response?.data?.message || 'Failed to send verification code.');
        }
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col overflow-hidden bg-slate-950 text-white">
      <div className="pointer-events-none absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[linear-gradient(135deg,#07111f_0%,#10223b_44%,#0a1727_100%)]" />
        <div className="absolute inset-0 opacity-[0.11] [background-size:28px_28px] [background-image:linear-gradient(to_right,rgba(226,232,240,0.28)_1px,transparent_1px),linear-gradient(to_bottom,rgba(226,232,240,0.28)_1px,transparent_1px)]" />
        <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-cyan-500/10 to-transparent" />
      </div>

      <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 items-center px-3 py-6 sm:px-6 sm:py-8 lg:px-8">
        <div className="grid w-full grid-cols-1 overflow-hidden rounded-2xl border border-white/10 bg-slate-950/70 shadow-2xl shadow-black/30 backdrop-blur md:grid-cols-2">
          <aside className="relative hidden overflow-hidden border-r border-white/10 bg-[#071126] p-10 md:flex md:flex-col md:justify-between">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_14%_12%,rgba(37,99,235,0.32),transparent_34%),linear-gradient(180deg,rgba(15,23,42,0)_0%,rgba(2,6,23,0.7)_100%)]" />
            <div className="pointer-events-none absolute -right-24 top-8 h-72 w-72 rotate-12 rounded-[2rem] border border-white/10 bg-white/[0.04]" />
            <div className="pointer-events-none absolute -left-16 bottom-16 h-44 w-44 rotate-[-14deg] rounded-[2rem] border border-cyan-200/10 bg-cyan-300/[0.04]" />

            <div className="relative">
              <div className="brand-logo-wrap mb-8">
                <img src={logo} alt="ShiftSmart" className="brand-logo w-44 drop-shadow-xl" />
              </div>
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg border border-white/15 bg-white/[0.08] text-lg text-cyan-100 shadow-xl shadow-black/20">
                <FaLock aria-hidden="true" />
              </div>
              <p className="text-xs font-bold uppercase tracking-wide text-cyan-100">Secure recovery</p>
              <h1 className="mt-3 text-3xl font-bold leading-tight text-white">Reset your password</h1>
              <p className="mt-3 max-w-sm text-sm leading-6 text-slate-200">
                Regain access quickly and securely using your registered email or phone number.
              </p>
            </div>

            <ul className="relative space-y-3 text-sm text-slate-200">
              <li className="flex min-h-[2.75rem] items-center gap-2 rounded-lg border border-white/10 bg-slate-950/25 px-3 py-2 backdrop-blur">
                <FaEnvelopeOpenText className="text-emerald-300" aria-hidden="true" />
                Verification code is sent instantly
              </li>
              <li className="flex min-h-[2.75rem] items-center gap-2 rounded-lg border border-white/10 bg-slate-950/25 px-3 py-2 backdrop-blur">
                <FaLock className="text-emerald-300" aria-hidden="true" />
                Secure multi-step recovery process
              </li>
            </ul>
          </aside>

          <section className="bg-white p-5 text-slate-900 sm:p-8 md:p-10 lg:p-12">
            <div className="mb-6 flex items-center justify-center md:hidden">
              <div className="brand-logo-wrap">
                <img src={logo} alt="ShiftSmart" className="brand-logo w-36 drop-shadow-xl" />
              </div>
            </div>

            <button
              type="button"
              className="mb-5 flex items-center text-sm font-medium text-slate-600 hover:text-blue-700"
              onClick={() => navigate(-1)}
            >
              <FaAngleLeft className="mr-2" /> Back
            </button>

            <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase text-blue-700">Account recovery</p>
                <h2 className="mt-1 text-2xl font-bold text-slate-950 sm:text-[1.7rem]">Forgot Password</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Enter your email or phone number to receive a verification code.
                </p>
              </div>
              <div className="inline-flex shrink-0 items-center gap-2 self-start whitespace-nowrap rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
                <FaShieldAlt aria-hidden="true" />
                Secure reset
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div className="space-y-2">
                <label htmlFor="email-or-phone" className="text-sm font-medium text-slate-700">Email or Phone</label>
                <input
                  id="email-or-phone"
                  type="text"
                  autoComplete="username"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="Enter your email or phone"
                  value={emailOrPhone}
                  onChange={(e) => {
                    setEmailOrPhone(e.target.value);
                    if (fieldError) setFieldError('');
                    if (error) setError('');
                  }}
                  onBlur={() => setFieldError(validateEmailOrPhone(emailOrPhone))}
                  disabled={loading}
                  required
                />
                {fieldError && <div className="text-xs text-red-600">{fieldError}</div>}
              </div>

              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-center text-xs text-red-700 sm:text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="w-full rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-950/30 transition-colors hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Code'}
              </button>

              <p className="text-center text-xs text-slate-500 md:text-left">
                Remembered your password?{' '}
                <Link to="/" className="font-medium text-blue-700 hover:text-blue-800 hover:underline">
                  Return to sign in
                </Link>
              </p>
            </form>
          </section>
        </div>
      </div>

      <div className="relative z-10 w-full shrink-0 px-3 pb-2 text-[11px] text-white/65 sm:px-6 lg:px-8">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-1.5 border-t border-white/10 pt-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="font-semibold text-white/80">© 2026 ShiftSmart. All rights reserved.</div>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Link to="/privacy-policy" className="hover:text-white hover:underline">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="hover:text-white hover:underline">Terms and Conditions</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
